import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../api";

const NAME_RE = /^[a-zA-Z\s]*$/;

function FieldError({ msg }) {
  if (!msg) return null;
  return <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4, fontWeight: 500, animation: "fadeIn 0.2s ease" }}>{msg}</div>;
}

export default function AuthScreen({ onBack, onAuth }) {
  const [mode,         setMode]         = useState("login");
  const [name,         setName]         = useState("");
  const [password,     setPassword]     = useState("");
  const [phone,        setPhone]        = useState("");
  const [error,        setError]        = useState("");
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shake,        setShake]        = useState(false);
  const [success,      setSuccess]      = useState(false);
  const [touched,      setTouched]      = useState({});
  
  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 500); };
  const touch = (field) => setTouched(t => ({ ...t, [field]: true }));

  const nameErr  = touched.name  && (!name.trim() ? "Name is required" : !NAME_RE.test(name) ? "Only letters allowed" : name.trim().length < 2 ? "Too short" : "");
  const passErr  = touched.pass  && (!password ? "Password is required" : password.length < 8 ? "Min 8 characters" : "");
  const phoneErr = touched.phone && phone && (!/^\d{10}$/.test(phone) ? "Must be 10 digits" : "");

  const handleNameChange = (e) => {
    if (NAME_RE.test(e.target.value)) setName(e.target.value);
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(val);
  };

  const isValid = mode === "login"
    ? name.trim() && password
    : name.trim() && NAME_RE.test(name) && password.length >= 8 && (!phone || phone.length === 10);

  const handleSubmit = async () => {
    setTouched({ name: true, pass: true, phone: true });
    if (!isValid) { triggerShake(); return; }
    setError(""); setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { name: name.trim(), password } : { name: name.trim(), password, phone: phone.trim() };
      const res = await fetch(`${API}${endpoint}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed"); triggerShake(); setLoading(false); return; }
      setSuccess(true);
      localStorage.setItem("fb_user", JSON.stringify(data.user));
      if (data.token) localStorage.setItem("fb_token", data.token);
      setTimeout(() => onAuth(data.user), 600);
    } catch { setError("Connection error"); triggerShake(); }
    setLoading(false);
  };
  const handleKeyDown = (e) => { if (e.key === "Enter") handleSubmit(); };

  return (
    <div className="auth-root" style={{ background: "#FAF8F5", minHeight: "100vh", display: "flex", position: "relative", overflow: "hidden" }}>
      <style>{`
        .auth-brand-col {
          display: none;
        }
        .auth-form-col {
          width: 100%;
          display: flex;
          flex-direction: column;
          padding: 40px 24px;
          background: #ffffff;
          position: relative;
          z-index: 2;
          overflow-y: auto;
        }
        @media (min-width: 1024px) {
          .auth-brand-col {
            width: 50%;
            display: flex !important;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: #111111;
            position: relative;
            z-index: 2;
            overflow: hidden;
            padding: 40px;
          }
          .auth-form-col {
            width: 50%;
            padding: 60px 80px;
          }
        }
        
        .auth-form-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 100%;
          max-width: 380px;
          margin: 0 auto;
        }
        
        .auth-input-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
        }

        .auth-input { 
          width: 100%; 
          background: #ffffff; 
          border: 1px solid #D1D5DB; 
          border-radius: 8px; 
          padding: 14px 16px; 
          color: #111; 
          font-family: 'Plus Jakarta Sans', sans-serif; 
          font-size: 14px; 
          outline: none; 
          transition: all 0.2s ease; 
        }
        .auth-input:focus { 
          border-color: var(--primary); 
          box-shadow: 0 0 0 4px rgba(217, 79, 0, 0.08); 
        }
        
        .auth-shake { animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }
      `}</style>

      {/* LEFT COLUMN: Brand Highlight Display */}
      <div className="auth-brand-col">
        {/* Soft atmospheric gradient glow */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 30%, rgba(217, 79, 0, 0.15) 0%, transparent 60%)", pointerEvents: "none" }} />
        
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: "420px", padding: "0 24px" }}>
          <div style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(38px, 4vw, 48px)", fontWeight: 800, color: "#ffffff", letterSpacing: "-1.5px", marginBottom: "16px" }}>
            Foody<span style={{ color: "var(--primary)" }}>Buddy</span>
          </div>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "16px", color: "rgba(255,255,255,0.5)", lineHeight: 1.6, fontWeight: 300, letterSpacing: "0.01em" }}>
            Pre-order from the canteen, pay instantly, and pick up when ready. Zero queues.
          </p>
        </div>
      </div>

      {/* RIGHT COLUMN: Credentials Form */}
      <div className="auth-form-col">
        {/* Back button */}
        <div style={{ marginBottom: "2rem" }}>
          <motion.button 
            onClick={onBack} 
            whileHover={{ scale: 1.1, x: -3 }}
            whileTap={{ scale: 0.95 }}
            style={{ background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "10px", width: "36px", height: "36px", color: "var(--dark)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}
          >
            ‹
          </motion.button>
        </div>

        <div className={`auth-form-container ${shake ? "auth-shake" : ""}`}>
          <div style={{ marginBottom: "32px" }}>
            <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: "32px", fontWeight: 700, color: "#111", marginBottom: "8px", letterSpacing: "-1px" }}>
              {mode === "login" ? "Welcome back" : "Create Account"}
            </h1>
            <p style={{ fontSize: "14px", color: "#666", fontWeight: 400 }}>
              {mode === "login" ? "Please enter your details to sign in." : "Start your journey with FoodyBuddy."}
            </p>
          </div>

          {error && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "12px", padding: "14px", color: "#EF4444", fontSize: "13px", marginBottom: "24px", textAlign: "center" }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div>
              <label className="auth-input-label">Full Name</label>
              <input className="auth-input" value={name} onChange={handleNameChange} onBlur={() => touch("name")} onKeyDown={handleKeyDown} placeholder="Enter your name" />
              <FieldError msg={nameErr} />
            </div>

            <AnimatePresence initial={false}>
              {mode === "register" && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: -12 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 0 }}
                  exit={{ opacity: 0, height: 0, marginTop: -12 }}
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <label className="auth-input-label">WhatsApp Number</label>
                  <input className="auth-input" value={phone} onChange={handlePhoneChange} onBlur={() => touch("phone")} onKeyDown={handleKeyDown} placeholder="10-digit mobile" inputMode="numeric" />
                  <FieldError msg={phoneErr} />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="auth-input-label">Password</label>
              <div style={{ position: "relative" }}>
                <input className="auth-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onBlur={() => touch("pass")} onKeyDown={handleKeyDown} placeholder="••••••••" style={{ paddingRight: "56px" }} />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)} 
                  style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <FieldError msg={passErr} />
            </div>

            <motion.button 
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={handleSubmit} 
              disabled={loading || success}
              style={{ marginTop: "12px", height: "48px", borderRadius: "8px", background: success ? "#16a34a" : "var(--primary)", border: "none", color: "white", cursor: "pointer", fontWeight: 700, fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: success ? "none" : "0 4px 12px rgba(217, 79, 0, 0.15)" }}
            >
              {success ? "Success!" : loading ? "Signing in..." : mode === "login" ? "Sign In" : "Register"}
            </motion.button>
          </div>

          <div style={{ marginTop: "32px", textAlign: "center", fontSize: "14px", color: "#4B5563" }}>
            {mode === "login" ? (
              <>Don't have an account? <span onClick={() => { setMode("register"); setError(""); setTouched({}); }} style={{ color: "var(--primary)", fontWeight: 700, cursor: "pointer", marginLeft: "4px" }}>Sign up</span></>
            ) : (
              <>Already have an account? <span onClick={() => { setMode("login"); setError(""); setTouched({}); }} style={{ color: "var(--primary)", fontWeight: 700, cursor: "pointer", marginLeft: "4px" }}>Sign in</span></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
